import {
  IconX,
  IconUser,
  IconMail,
  IconPhone,
  IconMapPin,
  IconUserCheck,
  IconTruck,
  IconClipboardText,
  IconInfoCircle,
  IconPackage,
  IconHistory,
  IconPhoto,
  IconCloudDown,
  IconCheck,
  IconCalendarClock,
} from "@tabler/icons-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ListView from "../../../components/common/ListView";
import NoDataFound from "../../../components/common/NoDataFound";
import formatDate, { formatDateWithTime } from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  pickup: any;
  returnToShipperDetails: any;
  isSidebar?: boolean;
  onUploadDoc?: () => void;
};

const Row = ({
  icon,
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-2 py-1 min-w-0 ${className || ""}`}>
    <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
      {icon && <div className="text-gray-400 flex-shrink-0">{icon}</div>}
      {label && (
        <span className="font-medium text-xs text-[#212121] whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
    <div className="text-right min-w-0 flex-1">
      <span className="text-xs text-[#212121] font-medium truncate block" title={typeof value === "string" ? value : undefined}>
        {value || ""}
      </span>
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

type PickupRequest = {
  id?: string;
  awbNumber?: string;
  awb?: string;
  status: string;
  [key: string]: any;
};

const ViewDetailsModal = ({
  open,
  onClose,
  pickup,
  returnToShipperDetails,
  isSidebar,
  onUploadDoc,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<"OVERVIEW" | "CARGO ITEMS" | "ACTIVITY LOG" | "POD MEDIA">("OVERVIEW");

  if (!open || !pickup) return null;

  const details = {
    ...pickup,
    ...returnToShipperDetails,
  };

  const shipmentsList = details?.shipments || [];
  const statusHistories = details?.statusHistories || [];

  const handleDownload = (docUrl?: string) => {
    if (!docUrl) return;
    window.open(docUrl, "_blank");
  };

  const renderMediaPreview = (url: string) => {
    const isPdf = url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("pdf");
    
    if (isPdf) {
      return (
        <div className="flex flex-col items-center gap-3 w-full p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <div className="p-3 bg-red-50 text-red-500 rounded-full">
            <IconClipboardText size={32} />
          </div>
          <p className="text-xs font-semibold text-slate-700">PDF Verification Document</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDownload(url)}
              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
            >
              Open / Download PDF
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-3 w-full p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
        <img
          src={url}
          alt="Proof of Return"
          className="max-h-40 object-contain rounded-lg border border-slate-200 bg-white p-1"
        />
        <button
          onClick={() => handleDownload(url)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
        >
          <IconCloudDown size={16} /> Open / Download Image
        </button>
      </div>
    );
  };

  const content = (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-200/50 pb-2">
        {([
          { key: "OVERVIEW", icon: <IconInfoCircle size={18} />, label: "Overview" },
          { key: "CARGO ITEMS", icon: <IconPackage size={18} />, label: "Cargo Items" },
          { key: "ACTIVITY LOG", icon: <IconHistory size={18} />, label: "Activity Log" },
          { key: "POD MEDIA", icon: <IconPhoto size={18} />, label: "POD Media" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 border ${
              activeTab === tab.key
                ? "bg-[#FBD037]/20 text-[#000000] border-gray-200 shadow-sm"
                : "bg-slate-100 hover:bg-slate-200/60 text-slate-600 border-gray-200"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <>
        {activeTab === "OVERVIEW" && (
          <div className="space-y-4">
            {/* Manifest Specifications Card */}
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <SectionHeader icon={IconClipboardText} title="Manifest Specifications" />
              <div className="space-y-2 relative">
                <Row label="Manifest ID" value={details.manifestId || details.id} />
                <Row
                  label="Status"
                  value={
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                      details.status === "COMPLETED"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}>
                      {(details.status || "").replace(/_/g, " ")}
                    </span>
                  }
                />
                <Row label="Type" value="Return To Shipper" />
                <Row label="Date" value={formatDate(details.createdAt || details.date)} />
                <Row label="Packages Quantity" value={details.qty || details.totalShipmentsCount} />
              </div>
            </div>

            {/* Sender & Address Information */}
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <SectionHeader icon={IconUser} title="Sender Information" />
              <div className="space-y-2 relative">
                <Row icon={<IconUser size={14} />} label="Client Name" value={details.clientName || details.client} />
                <Row icon={<IconPhone size={14} />} label="Phone" value={details.mobileNo || details.clientMobile} />
                <Row icon={<IconMail size={14} />} label="Email" value={details.email} />
                <Row icon={<IconMapPin size={14} />} label="Pickup Location" value={details.location || details.fullAddress || details.locationName} />
              </div>
            </div>

            {/* Driver Details */}
            {(details.driverCode || details.driverName) && (() => {
              const assignmentHistory = statusHistories.find(
                (h: any) => h.newStatus === "ASSIGNED" || h.newStatus === "ASSIGNED_TO_DRIVER"
              );
              return (
                <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconUserCheck} title="Driver & Vehicle Assignments" />
                  <div className="space-y-2 relative">
                    <Row icon={<IconUserCheck size={14} />} label="Driver Name" value={details.driverName || details.assignedResource} />
                    <Row icon={<IconClipboardText size={14} />} label="Driver Code" value={details.driverCode} />
                    <Row icon={<IconPhone size={14} />} label="Contact" value={details.driverMobileNo || details.driverMobile} />
                    <Row icon={<IconTruck size={14} />} label="Vehicle" value={details.vehicleType || "Mini Truck"} />
                    {assignmentHistory && (
                      <>
                        <Row
                          icon={<IconUser size={14} />}
                          label={t("Assigned By")}
                          value={assignmentHistory.changedByName || "—"}
                        />
                        <Row
                          icon={<IconCalendarClock size={14} />}
                          label={t("Assigned At")}
                          value={assignmentHistory.createdAt ? formatDateWithTime(assignmentHistory.createdAt) : "—"}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Uploaded Document Card */}
            {details.image && (
              <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
                      <IconPhoto size={16} className="text-[#212121]" stroke={1.5} />
                    </div>
                    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
                      Uploaded Document
                    </p>
                  </div>
                  {onUploadDoc && (
                    <button
                      onClick={onUploadDoc}
                      className="px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-all cursor-pointer"
                    >
                      Change Doc
                    </button>
                  )}
                </div>
                <div className="relative">
                  {renderMediaPreview(details.image)}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "CARGO ITEMS" && (
          <div className="space-y-4 font-sans text-xs">
            {/* ASSOCIATED WAYBILLS SECTION */}
            <div className="space-y-3">
              <SectionHeader icon={IconPackage} title={`Associated Waybills (${shipmentsList.length})`} />
              {shipmentsList.length > 0 ? (
                <div className="space-y-3">
                  {shipmentsList.map((awb: any, index: number) => {
                    const formattedStatus = (awb.status || "")
                      .toLowerCase()
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char: string) => char.toUpperCase());

                    const getStatusClasses = (status: string) => {
                      const lower = (status || "").toLowerCase();
                      if (lower.includes("delivery") || lower.includes("dispatch") || lower.includes("loaded")) {
                        return "bg-green-50 text-green-700 border-green-200";
                      } else if (lower.includes("delivered") || lower.includes("completed")) {
                        return "bg-gray-50 text-gray-700 border-gray-200";
                      } else if (lower.includes("undelivered")) {
                        return "bg-amber-50 text-amber-700 border-amber-200";
                      }
                      return "bg-blue-50 text-blue-700 border-blue-200";
                    };

                    const dest = [awb.receiverCity, awb.receiverDistrict, awb.receiverCountry]
                      .filter(Boolean)
                      .join(", ") || "—";

                    const items = awb.olineItems || awb.oLineItems || awb.items || awb.products || [];

                    return (
                      <div
                        key={awb.id || index}
                        className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 shadow-sm hover:shadow-md space-y-2 text-xs"
                      >
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                        <div className="relative space-y-2">
                          <div className="flex justify-between items-center">
                            <span
                              onClick={() => navigate(`/orders/${awb.awbNumber || awb.awb}`)}
                              className="font-bold text-sm text-blue-600 hover:underline cursor-pointer"
                            >
                              {awb.awbNumber || awb.awb || "—"}
                            </span>
                            <span className={`rounded-full px-2.5 py-0.5 border text-[10px] font-semibold ${getStatusClasses(awb.status)}`}>
                              {formattedStatus || "Loaded"}
                            </span>
                          </div>
                          <div className="text-slate-500 font-medium">
                            Customer: <span className="text-slate-700">{awb.customerName || awb.receiverName || "—"}</span> • Dest: <span className="text-slate-700">{dest}</span>
                          </div>
                          <div className="flex justify-between items-center text-[11px] text-slate-400">
                            <div>
                              Weight: <span className="text-slate-600 font-medium">{awb.weight ? `${awb.weight} kg` : "—"}</span>
                              {" • "}
                              Pcs: <span className="text-slate-600 font-medium">{awb.pieces || awb.pcs || "1"}</span>
                            </div>
                            <div className="font-semibold text-slate-600 text-xs">
                              {(() => {
                                const pType = (awb.paymentType || awb.payment_type || "").toUpperCase();
                                if (pType === "COD" || (!pType && awb.codAmount && Number(awb.codAmount) > 0)) {
                                  return `${t("COD")}: ${awb.codAmount != null ? awb.codAmount : "N/A"}`;
                                }
                                if (pType === "CC") {
                                  return t("CC Paid");
                                }
                                return pType || t("CC Paid");
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full p-6 border border-[#D2D2D2]/70 rounded-2xl bg-gradient-to-b from-white/90 to-white/60 shadow-sm text-center text-xs text-slate-400">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
                  <div className="relative">
                    No Associated Waybills
                    <div className="mt-1 text-[11px] text-slate-400 font-medium">No raw physical shipments registered to this manifest file.</div>
                  </div>
                </div>
              )}
            </div>


          </div>
        )}

        {activeTab === "ACTIVITY LOG" && (
          <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
            <div className="relative">
              <SectionHeader icon={IconHistory} title="Audit Trail History Timestamps" />
              {statusHistories.length > 0 ? (
                <div className="space-y-0 relative pl-4 border-l border-emerald-200/60 ml-2">
                  {statusHistories.map((item: any, index: number) => {
                    const isLast = index === statusHistories.length - 1;
                    return (
                      <div key={item.id || index} className="relative pb-6 last:pb-0">
                        {/* Dot Indicator */}
                        {isLast ? (
                          <div className="absolute -left-[24px] top-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md border border-white ring-2 ring-emerald-600/30">
                            <IconCheck size={10} stroke={4} />
                          </div>
                        ) : (
                          <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-500/20 shadow-xs" />
                        )}
                        <div>
                          <p className="text-xs font-semibold text-slate-800">
                            {(item.newStatus || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </p>
                          {item.details && (
                            <p className="text-[11px] text-slate-500 mt-0.5">{item.details}</p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-1">
                            {formatDateWithTime(item.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-xs text-slate-400">
                  No activity history logs found.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "POD MEDIA" && (
          <div className="space-y-4">
            {/* Proof of Return signature & photo */}
            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="relative">
                <SectionHeader icon={IconClipboardText} title="Proof of Return Signature" />
                <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <div className="text-center py-2 text-slate-400 flex flex-col items-center gap-2">
                    <IconClipboardText size={32} className="opacity-30 text-[#212121]" />
                    <p className="text-xs italic">Digital Signature Signed Off</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Signature details available on complete report download</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="relative">
                <SectionHeader icon={IconPhoto} title="Cargo Photo Verification (Proof of Pickup)" />
                <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  {details.image ? (
                    renderMediaPreview(details.image)
                  ) : (
                    <div className="text-center py-4 text-slate-400 flex flex-col items-center gap-2">
                      <IconPhoto size={32} className="opacity-30 text-[#212121]" />
                      <p className="text-xs italic">No verification photo media available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );

  if (isSidebar) {
    return <div className="p-6">{content}</div>;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="rounded-3xl w-full md:w-10/12 lg:w-9/12 bg-white flex flex-col max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-800">
            View Request: {details.manifestId || details.id}
          </h1>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{content}</div>
      </div>
    </div>
  );
};

export default ViewDetailsModal;
